import { useSyncExternalStore } from 'react';
import type { WorkItemStore } from './WorkItemStore';

/**
 * T-0770 (`docs/concept-mobile-app.md`, LC-6c): where the work-item store lives, so the launch entry
 * does not carry it.
 *
 * `WorkItemStore` (and `WorkItemApi` behind it) was built when `stores/index.ts` loaded, in the entry,
 * though no first screen draws an item list. It is built now by `workItemStoreInstance.ts`, a module
 * every screen that uses it imports statically, so on those screens the root store's synchronous
 * `workItemStore` always finds it. A file in the entry never reads that getter
 * (`workItemStoreSlot.guard.test.ts`):
 * - to act on the store, it awaits `loadWorkItemStore()`, which loads the store on first call;
 * - to read what the store holds, or to act only if there is anything to act on, it asks
 *   `peekWorkItemStore()` (or `usePeekedWorkItemStore()` while rendering), which is undefined until
 *   the store has loaded: an unloaded store has nothing to show, scope or drop.
 */
let instance: WorkItemStore | undefined;
let loading: Promise<WorkItemStore> | null = null;
const listeners = new Set<() => void>();

/** Called once, by `workItemStoreInstance.ts`, with the store it built. */
export function provideWorkItemStore(store: WorkItemStore): void {
  instance = store;
  for (const listener of listeners) listener();
}

/** The store if it has loaded, else undefined. */
export function peekWorkItemStore(): WorkItemStore | undefined {
  return instance;
}

/** The store, loading it on first call; one load, shared by every caller. */
export function loadWorkItemStore(): Promise<WorkItemStore> {
  if (instance) return Promise.resolve(instance);
  loading ??= import('./workItemStoreInstance')
    .then((module) => module.workItemStore)
    .catch((error: unknown) => {
      // A failed load (a deployment replaced under the tab) is tried again by the next caller.
      loading = null;
      throw error;
    });
  return loading;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `peekWorkItemStore()` for a component, drawn again when the store loads. */
export function usePeekedWorkItemStore(): WorkItemStore | undefined {
  return useSyncExternalStore(subscribe, peekWorkItemStore, peekWorkItemStore);
}

/** A screen that read the synchronous getter before any screen had loaded the store. */
export class WorkItemStoreNotLoadedError extends Error {
  constructor() {
    super(
      'The work-item store was read before it loaded: import "@/stores/workItemStoreInstance" in the ' +
        'screen that reads it, or await loadWorkItemStore() (T-0770, LC-6c).',
    );
    this.name = 'WorkItemStoreNotLoadedError';
  }
}

/** The root store's synchronous `workItemStore`: for screens that load the store with themselves. */
export function workItemStoreOrThrow(): WorkItemStore {
  if (!instance) throw new WorkItemStoreNotLoadedError();
  return instance;
}
